import { jsPDF } from 'jspdf';

interface PurchaserInfo {
  name: string;
}

interface ColorSelectionAmendmentData {
  projectName: string;
  unitNumber: string;
  level: string;
  suiteNo: string;
  purchasers: PurchaserInfo[];
  colorCollectionName: string;
  vendorName: string;
  colorSwatchImage?: string; // Base64 image data for color collection hero image
  projectLogo?: string; // Base64 image data for project logo
}

export const generateColorSelectionAmendment = (data: ColorSelectionAmendmentData): void => {
  const doc = new jsPDF();

  // Set font
  doc.setFont('helvetica');

  // Project Logo or Title
  if (data.projectLogo) {
    try {
      // Add project logo image
      doc.addImage(data.projectLogo, 'PNG', 20, 15, 60, 20);
    } catch (err) {
      console.error('Error adding project logo:', err);
      // Fallback to text if image fails
      doc.setFontSize(24);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(139, 115, 85);
      doc.text(data.projectName.toUpperCase(), 20, 25);
    }
  } else {
    // Fallback: Title - Project Name styled like "TANU" in the PDF
    doc.setFontSize(24);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(139, 115, 85); // Brown color similar to the PDF
    doc.text(data.projectName.toUpperCase(), 20, 25);
  }

  // Section Header
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('AMENDMENT', 20, 40);

  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('COLOUR SELECTION', 20, 47);

  // Project name subtitle
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(data.projectName, 20, 54);

  // Right side info box
  const boxX = 150;
  const boxY = 35;
  const boxWidth = 45;
  const boxLineHeight = 7;

  doc.setDrawColor(200, 200, 200);
  doc.rect(boxX, boxY, boxWidth, boxLineHeight * 3);

  // Box content
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Dwelling Unit:', boxX + 2, boxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.text(data.unitNumber, boxX + boxWidth - 15, boxY + 5);

  doc.setFont('helvetica', 'bold');
  doc.text('Level:', boxX + 2, boxY + 12);

  doc.setFont('helvetica', 'bold');
  doc.text('Suite No', boxX + 2, boxY + 19);
  doc.setFont('helvetica', 'normal');
  doc.text(data.suiteNo, boxX + boxWidth - 15, boxY + 19);

  // Main text content
  let currentY = 70;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const purchasersText = data.purchasers.map(p => p.name).join(', ');

  const introText = `This is an amendment to an Agreement of Purchase and Sale made between ${data.vendorName} (the "Vendor") and ${purchasersText}, (the "Purchaser(s)") relating to an Agreement of Purchase and Sale for Suite ${data.suiteNo}, Unit ${data.unitNumber} on Level (the "Purchase Agreement").`;

  const splitIntro = doc.splitTextToSize(introText, 170);
  doc.text(splitIntro, 20, currentY);
  currentY += splitIntro.length * 5 + 5;

  // Section A
  doc.setFont('helvetica', 'bold');
  doc.text('A.', 20, currentY);
  doc.setFont('helvetica', 'normal');

  const sectionAText = `Pursuant to the Schedule "D" Standard Features Park Collection to the Agreement of Purchase and Sale and as displayed and described in the ${data.colorCollectionName} attached here to, the Purchaser hereby selects the following interior colour scheme:`;
  const splitA = doc.splitTextToSize(sectionAText, 165);
  doc.text(splitA, 25, currentY);
  currentY += splitA.length * 5 + 10;

  // Color Collection Hero Image
  if (data.colorSwatchImage) {
    try {
      // Display color collection hero image with larger, more prominent sizing
      doc.addImage(data.colorSwatchImage, 'PNG', 40, currentY, 130, 40);
      currentY += 45;
    } catch (err) {
      console.error('Error adding color collection hero image:', err);
      // Fallback to placeholder if image fails
      doc.setDrawColor(150, 150, 150);
      doc.setFillColor(245, 245, 245);
      doc.rect(70, currentY, 70, 20, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Color Collection Image', 95, currentY + 12);
      currentY += 30;
    }
  } else {
    // Draw placeholder rectangle for color collection hero image
    doc.setDrawColor(150, 150, 150);
    doc.setFillColor(245, 245, 245);
    doc.rect(70, currentY, 70, 20, 'FD');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Color Collection Image', 95, currentY + 12);
    currentY += 30;
  }

  // Section B
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('B.', 20, currentY);
  doc.setFont('helvetica', 'normal');

  const sectionBText = `The Purchaser acknowledges that the ${data.colorCollectionName} finish above is an artistic representation only and that variations from what is displayed in the ${data.colorCollectionName} finish above may occur`;
  const splitB = doc.splitTextToSize(sectionBText, 165);
  doc.text(splitB, 25, currentY);
  currentY += splitB.length * 5 + 5;

  // Section C
  doc.setFont('helvetica', 'bold');
  doc.text('C.', 20, currentY);
  doc.setFont('helvetica', 'normal');

  const sectionCText = 'The parties acknowledge and agree that this Amendment may be executed in counterparts and by means of electronic transmission.';
  const splitC = doc.splitTextToSize(sectionCText, 165);
  doc.text(splitC, 25, currentY);
  currentY += splitC.length * 5 + 10;

  // Signature section
  currentY = Math.max(currentY, 220); // Ensure signatures are at bottom half

  doc.setFontSize(9);
  doc.text('Dated this __________ day of _____________, 2025', 20, currentY);
  currentY += 10;
  doc.text('Dated this __________ day of _____________, 2025', 20, currentY);
  currentY += 15;

  // Left column - Witness
  doc.text('________________________________________', 20, currentY);
  currentY += 5;
  doc.text('Witness Signature', 20, currentY);

  // Right column - Vendor
  let rightY = currentY - 5;
  doc.text(data.vendorName, 120, rightY - 10);
  rightY += 5;
  doc.text('Per : ___________________________________', 120, rightY);
  rightY += 5;
  doc.text('Title : Authorized Signing Officer', 120, rightY);
  rightY += 5;
  doc.text('I have authority to bind the Corporation.', 120, rightY);

  // Purchaser signatures
  currentY += 15;
  data.purchasers.forEach((purchaser, index) => {
    // Check if we need a new page (leaving margin of 20 from bottom)
    if (currentY > 270) {
      doc.addPage();
      currentY = 20; // Start from top of new page

      // Add page header on continuation page
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Purchaser Signatures (continued)', 20, currentY);
      currentY += 15;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
    }

    doc.text('________________________________________', 120, currentY);
    currentY += 5;
    doc.text(purchaser.name, 120, currentY);
    currentY += 10;
  });

  // Save the PDF
  const fileName = `Color_Selection_Amendment_Unit_${data.unitNumber}_${new Date().getTime()}.pdf`;
  doc.save(fileName);
};

// Placeholder for other document generators
export const generateAmendment = (data: any): void => {
  // TODO: Implement general amendment
  console.log('Generate Amendment - Coming soon', data);
};

export const generateRevisedAmendment = (data: any): void => {
  // TODO: Implement revised amendment
  console.log('Generate Revised Amendment - Coming soon', data);
};

export const generateInvoice = (data: any): void => {
  // TODO: Implement invoice
  console.log('Generate Invoice - Coming soon', data);
};
